import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Mail, User, CreditCard, Shield, Bug, ExternalLink,
} from "lucide-react";
import { LEGAL_CONTACT_EMAIL } from "@/lib/legal";

// Public URL: /support
// Accessible without login. Used for App Store / Play Store submissions
// as the "Support URL". Do NOT gate behind auth.

const APP_STORE_NAME = "IX NOVA";
const SUPPORT_EMAIL  = LEGAL_CONTACT_EMAIL; // "minhichris64@gmail.com"

const SECTIONS = [
  {
    icon: User,
    title: "Account & Login",
    body: "Can't sign in, forgot your password, need to change your email address, or want to delete your account? Most account tasks can be handled inside the app under Profile → Edit Account.",
    tips: [
      "Forgot password? Tap “Forgot password?” on the sign-in screen and check your inbox.",
      "Email not arriving? Check spam/promotions, then tap Resend from Edit Account.",
      "Want to delete your account? Visit /delete-account for the request form.",
    ],
  },
  {
    icon: CreditCard,
    title: "Subscriptions & Billing",
    body: `${APP_STORE_NAME} Premium is billed by Stripe on the web and by Apple on the App Store. You can cancel or manage your subscription at any time — no cancellation fees, no lock-in.`,
    tips: [
      "Web subscribers: manage or cancel from Profile → Manage subscription.",
      "iPhone subscribers: manage in Settings → Apple ID → Subscriptions.",
      "Refunds are handled per Apple's or Stripe's standard refund policy.",
    ],
  },
  {
    icon: Shield,
    title: "Privacy & Data",
    body: `${APP_STORE_NAME} takes privacy seriously. Your photos and personal information are used only to power your coaching experience and are never sold. Full details are in our Privacy Policy.`,
    tips: [
      "Request a copy of your data by emailing us — we reply within 30 days.",
      "Delete your account and personal data at any time via /delete-account.",
      "We comply with GDPR, CCPA, and COPPA.",
    ],
  },
  {
    icon: Bug,
    title: "Technical Issues",
    body: "Running into a bug, a crash, or a feature that isn't working the way you expected? We'd love to know so we can fix it fast.",
    tips: [
      "Include your device model + OS version (e.g. iPhone 15 Pro, iOS 17.4).",
      "Describe the steps you took and what you expected to see.",
      "Screenshots or short screen recordings help us diagnose faster.",
    ],
  },
];

function SectionCard({ icon: Icon, title, body, tips }) {
  return (
    <div
      style={{
        background: "var(--bg-elev-1, #141414)",
        border: "1px solid var(--line, #1f1f1f)",
        borderRadius: 14,
        padding: "18px 20px",
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 34, height: 34, borderRadius: "50%",
            background: "rgba(232,211,185,0.10)",
            color: "var(--gold, #e8d3b9)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={16} strokeWidth={1.7} />
        </div>
        <h3
          className="h3"
          style={{
            color: "var(--text)", margin: 0, fontSize: 16, lineHeight: 1.2,
          }}
        >
          {title}
        </h3>
      </div>
      <p
        className="body-sm"
        style={{
          color: "var(--text-dim)", lineHeight: 1.6, marginTop: 0, marginBottom: 10,
        }}
      >
        {body}
      </p>
      <ul
        style={{
          margin: 0,
          paddingLeft: 20,
          color: "var(--text-dim)",
          fontSize: 13,
          lineHeight: 1.7,
        }}
      >
        {tips.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>
    </div>
  );
}

export default function SupportScreen() {
  const navigate = useNavigate();

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `${APP_STORE_NAME} Support`
  )}&body=${encodeURIComponent(
    "Hi IX NOVA team,\n\n[Tell us how we can help. If it's a bug, please include your device model + iOS/Android version + steps to reproduce.]\n\nThanks."
  )}`;

  return (
    <div
      className="screen fade-in"
      data-testid="support-page"
      style={{ paddingBottom: 60 }}
    >
      <div className="top-bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="support-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">SUPPORT</div>
        <div style={{ width: 32 }} />
      </div>

      <div className="eyebrow" style={{ marginTop: 12, marginBottom: 8 }}>
        HELP CENTER
      </div>
      <h1
        className="h1"
        style={{
          fontSize: 30, marginBottom: 12, lineHeight: 1.12,
        }}
      >
        {APP_STORE_NAME} <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Support</em>
      </h1>
      <p
        className="body"
        style={{
          color: "var(--text-dim)", marginTop: 0, marginBottom: 22, lineHeight: 1.55,
        }}
      >
        Need help with {APP_STORE_NAME}? We&#39;re here to help.
      </p>

      {/* Primary CTA */}
      <a
        href={mailtoHref}
        data-testid="support-contact-btn"
        className="btn btn-primary"
        style={{
          width: "100%", justifyContent: "center",
          display: "flex", alignItems: "center", gap: 10,
          textDecoration: "none", marginBottom: 8,
        }}
      >
        <Mail size={15} strokeWidth={1.9} />
        <span>Contact Support</span>
      </a>
      <p
        className="tiny"
        style={{
          color: "var(--text-faint)",
          textAlign: "center",
          marginTop: 6, marginBottom: 24,
        }}
      >
        Or email us directly at{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          style={{ color: "var(--gold, #e8d3b9)" }}
          data-testid="support-email-link"
        >
          {SUPPORT_EMAIL}
        </a>
      </p>

      {/* Sections */}
      {SECTIONS.map((s) => (
        <SectionCard key={s.title} {...s} />
      ))}

      {/* Legal links */}
      <div
        style={{
          background: "var(--bg-elev-1, #141414)",
          border: "1px solid var(--line, #1f1f1f)",
          borderRadius: 14,
          padding: "16px 20px",
          marginTop: 4,
        }}
      >
        <div
          className="eyebrow"
          style={{ marginBottom: 12, fontSize: 11, letterSpacing: ".22em" }}
        >
          LEGAL
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <a
            href="/privacy-policy"
            data-testid="support-privacy-link"
            style={{
              color: "var(--text)",
              textDecoration: "none",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "6px 0",
              borderBottom: "1px solid var(--line, #1f1f1f)",
            }}
          >
            <span style={{ fontSize: 14 }}>Privacy Policy</span>
            <ExternalLink size={14} strokeWidth={1.7} color="var(--text-faint)" />
          </a>
          <a
            href="/legal/terms"
            data-testid="support-terms-link"
            style={{
              color: "var(--text)",
              textDecoration: "none",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "6px 0",
            }}
          >
            <span style={{ fontSize: 14 }}>Terms of Service</span>
            <ExternalLink size={14} strokeWidth={1.7} color="var(--text-faint)" />
          </a>
        </div>
      </div>

      <p
        className="tiny"
        style={{
          color: "var(--text-faint)",
          textAlign: "center",
          marginTop: 26,
          lineHeight: 1.7,
        }}
      >
        {APP_STORE_NAME}: AI Personal Coach
      </p>
    </div>
  );
}
